import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import Combobox from "@github/combobox-nav"
import debounce from "just-debounce-it"

// Live-typing search inside the global search modal (app/views/admin/search).
// Mirrors @stiege10/frontend-kit's command_controller.js (debounce + Combobox
// + Turbo.visit-on-commit), except this one fetches from the server on every
// keystroke instead of filtering rows already on the page — the ranking and
// German-keyword/month/year parsing (Admin::Search::Query/Parser) only exist
// server-side, so there is nothing to filter client-side.
//
// Registered as "admin-search" (see app/javascript/controllers/index.js) to
// match the `data-admin-search-target` attributes already baked into
// app/views/admin/search/show.html.erb and its partials by the prior unit.
export default class extends Controller {
  static targets = ["input", "results", "status", "loading", "error", "chip", "result", "empty", "limit"]

  initialize() {
    this.debouncedSearch = debounce(this.performSearch.bind(this), 250)
  }

  connect() {
    // R1: typeable the instant the modal opens, no click required.
    this.inputTarget.focus()
    this.#startCombobox()
  }

  disconnect() {
    this.debouncedSearch.cancel()
    this.combobox?.destroy()
    this.abortController?.abort()
  }

  // input->admin-search#search
  search() {
    this.debouncedSearch()
  }

  // submit->admin-search#submit, on the form. Results already follow every
  // keystroke and checkbox, so a submit (Enter with no row to open) has
  // nothing to add — and left alone it would reload the frame and let the
  // modal close itself on the "successful" submit.
  submit(event) {
    event.preventDefault()
    this.debouncedSearch.cancel()
    this.performSearch()
  }

  // change->admin-search#chipChange, on every type checkbox. Unlike the
  // former submit-button chips, a checkbox's own `change` never triggers
  // form submission on its own — nothing to preventDefault — so this only
  // has to re-search immediately, no debounce delay for a discrete action.
  chipChange() {
    this.performSearch()
  }

  // Reached two ways, and both must fire exactly one navigation:
  // - combobox-commit->admin-search#commit on the results listbox: fired by
  //   combobox-nav on arrow-down + Enter (it simulates a click on the active
  //   option, then dispatches this bubbling from that option element).
  // - click->admin-search#commit on the row's own <a> (result_row.html.erb):
  //   a real pointer click. Stopping propagation there keeps the click from
  //   ever reaching combobox-nav's own list-level click listener, which would
  //   otherwise dispatch combobox-commit for the same click and run this
  //   method a second time. preventDefault stops both the anchor's native
  //   navigation and Turbo Drive's own click-to-visit handling.
  commit(event) {
    event.preventDefault()
    if (event.type === "click") event.stopPropagation()

    const option = event.target.closest('[role="option"]')
    const href = option?.dataset.href
    if (href) Turbo.visit(href, { action: "advance" })
  }

  async performSearch() {
    // R10/KTD15: every keystroke, chip click, or clear aborts whatever is
    // still in flight before starting the next request.
    this.abortController?.abort()

    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      // R5/KTD14: no fetch below 2 characters, and nothing stale stays shown.
      this.#clearResults()
      return
    }

    this.abortController = new AbortController()
    this.#showLoading()

    try {
      // X-Requested-With tells Admin::SearchController#show to render just
      // the results fragment (this method only ever needs #admin-search-results,
      // not the full page/modal chrome around it, which would otherwise
      // re-render server-side and re-download on every debounced keystroke).
      const response = await fetch(this.#searchUrl(query), {
        signal: this.abortController.signal,
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!response.ok) throw new Error(`admin search request failed: ${response.status}`)

      this.#renderResults(await response.text())
      this.#hideLoading()
    } catch (error) {
      if (error.name === "AbortError") {
        // R18: an aborted request is not a failure — just stop loading.
        this.#hideLoading()
        return
      }

      this.#hideLoading()
      // Don't leave the previous successful search's rows sitting under the
      // error banner — combobox-nav would still let the user arrow-key/Enter
      // into those stale, unverified rows.
      this.resultTargets.forEach((row) => row.remove())
      this.emptyTargets.forEach((el) => el.remove())
      this.limitTargets.forEach((el) => el.remove())
      this.#showError()
    }
  }

  #searchUrl(query) {
    const url = new URL(this.inputTarget.form.action)
    url.searchParams.set("q", query)

    this.#activeEntities().forEach((entity) => url.searchParams.append("entity[]", entity))

    return url.toString()
  }

  // The checkboxes' own checked state is the source of truth — nothing to
  // sync server-side markup against, unlike the old single-select chips.
  #activeEntities() {
    return this.chipTargets.filter((chip) => chip.checked).map((chip) => chip.value)
  }

  // The response is the same full show/_results markup the server always
  // renders (R18/R19/R20/R22 all live there) — pull out just the results
  // fragment and drop it in, rather than re-rendering rows in JS.
  #renderResults(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html")
    const fresh = parsed.getElementById("admin-search-results")
    if (!fresh) return

    this.resultsTarget.replaceWith(fresh)
    // The old option elements are gone with it — combobox-nav has to be
    // rebuilt against the new ones (R21: the top-ranked row pre-highlighted).
    this.#startCombobox()
  }

  #clearResults() {
    this.resultTargets.forEach((row) => row.remove())
    this.emptyTargets.forEach((el) => el.remove())
    this.limitTargets.forEach((el) => el.remove())
    this.statusTarget.textContent = ""
    this.loadingTarget.hidden = true
    this.errorTarget.hidden = true
  }

  #showLoading() {
    this.loadingTarget.hidden = false
    this.errorTarget.hidden = true
  }

  #hideLoading() {
    this.loadingTarget.hidden = true
  }

  #showError() {
    this.errorTarget.hidden = false
  }

  #startCombobox() {
    this.combobox?.destroy()
    // R21: pre-highlights the top result as "active" (not "selected") so a
    // bare Enter with no prior arrow press still commits to it.
    this.combobox = new Combobox(this.inputTarget, this.resultsTarget, { firstOptionSelectionMode: "active" })
    this.combobox.start()
  }
}
